import React, { useState } from 'react';
import { Button } from './Button';
import { Phone, Mail, CheckCircle, Lock, ArrowRight, Server, Zap, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import { FixelsBadge } from './Fixels';

export const ContactFooter: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <footer id="contact" className="bg-slate-50 border-t border-slate-100">
      <div className="py-16 md:py-24 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          
          {/* Info Side */}
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4 md:mb-6 tracking-tight">Vraag gratis advies aan</h2>
            <p className="text-slate-600 mb-8 md:mb-10 leading-relaxed text-base md:text-lg font-light">
              Laat uw gegevens achter. Een gespecialiseerde jurist belt u (binnen 1 werkdag) terug om uw situatie te bespreken. Dit gesprek is altijd <span className="font-medium text-slate-900">vrijblijvend</span>.
            </p>

            <div className="space-y-4 md:space-y-6">
              <a href="tel:0201234567" className="flex items-start gap-5 group p-4 -ml-4 rounded-xl hover:bg-white hover:shadow-sm transition-all duration-200">
                <div className="w-12 h-12 bg-white rounded-xl border border-slate-100 shadow-sm flex items-center justify-center text-brand-700 group-hover:border-brand-100 group-hover:text-brand-600 transition-colors">
                  <Phone size={22} />
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-0.5 group-hover:text-brand-600 transition-colors">Telefonisch contact</p>
                  <p className="text-lg md:text-xl font-medium text-slate-900">020 - 123 45 67</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Bereikbaar op werkdagen van 09:00 - 17:30
                  </p>
                </div>
              </a>
              
              <a href="mailto:hulp@letselhulpdirect.nl" className="flex items-start gap-5 group p-4 -ml-4 rounded-xl hover:bg-white hover:shadow-sm transition-all duration-200">
                <div className="w-12 h-12 bg-white rounded-xl border border-slate-100 shadow-sm flex items-center justify-center text-brand-700 group-hover:border-brand-100 group-hover:text-brand-600 transition-colors">
                  <Mail size={22} />
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-0.5 group-hover:text-brand-600 transition-colors">E-mail</p>
                  <p className="text-lg md:text-xl font-medium text-slate-900">hulp@letselhulpdirect.nl</p>
                </div>
              </a>
            </div>

            <div className="mt-12 md:mt-16 pt-8 border-t border-slate-200/60">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Erkende Specialisten</p>
              <div className="flex flex-wrap gap-3">
                <div className="px-3 py-1.5 bg-white border border-slate-200 rounded text-sm font-semibold text-slate-600 shadow-sm">
                  NIVRE
                </div>
                 <div className="px-3 py-1.5 bg-white border border-slate-200 rounded text-sm font-semibold text-slate-600 shadow-sm">
                  Keurmerk Letselschade
                </div>
                 <div className="px-3 py-1.5 bg-white border border-slate-200 rounded text-sm font-semibold text-slate-600 shadow-sm">
                  LSA
                </div>
              </div>
            </div>
          </div>

          {/* Form Side */}
          <div className="bg-white rounded-2xl p-6 md:p-10 shadow-xl shadow-slate-200/50 border border-slate-100 min-h-[500px] flex flex-col justify-center">
            {submitted ? (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-left"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Aanvraag succesvol</h3>
                    <p className="text-sm text-slate-500">Dit is een demo-omgeving.</p>
                  </div>
                </div>

                {/* Concept Simulation Card */}
                <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden mb-6">
                  <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 flex justify-between items-center">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Backend Simulatie</span>
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                      <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                    </div>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="flex items-start gap-3">
                      <Server size={16} className="text-brand-600 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-slate-800">CRM Koppeling</p>
                        <p className="text-xs text-slate-500 leading-snug">Lead data wordt automatisch gesynchroniseerd met uw CRM (Salesforce, HubSpot, etc.).</p>
                      </div>
                    </div>
                     <div className="flex items-start gap-3">
                      <Zap size={16} className="text-yellow-600 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Lead Scoring: High Priority</p>
                        <p className="text-xs text-slate-500 leading-snug">Op basis van de wizard-antwoorden krijgt deze lead prioriteit A (hoge conversiekans).</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MessageSquare size={16} className="text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Directe opvolging</p>
                        <p className="text-xs text-slate-500 leading-snug">Cliënt ontvangt direct een bevestigingsmail in uw huisstijl.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                    Benieuwd hoe wij dit proces voor uw kantoor kunnen automatiseren?
                  </p>
                  <a 
                    href="https://fixels.nl" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-full px-5 py-3 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors gap-2 group"
                  >
                    Bekijk Fixels <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                  </a>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1.5">Naam</label>
                  <input 
                    type="text" 
                    id="name" 
                    required
                    className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:bg-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all placeholder:text-slate-400"
                    placeholder="Uw naam"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1.5">Telefoonnummer</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    required
                    className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:bg-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all placeholder:text-slate-400"
                    placeholder="06 12345678"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">E-mailadres</label>
                  <input 
                    type="email" 
                    id="email" 
                    className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:bg-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all placeholder:text-slate-400"
                    placeholder="naam@voorbeeld.nl"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-baseline mb-1.5">
                     <label htmlFor="message" className="block text-sm font-medium text-slate-700">Opmerking</label>
                     <span className="text-xs text-slate-400 font-light">Optioneel</span>
                  </div>
                  <textarea 
                    id="message" 
                    rows={2}
                    className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:bg-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all resize-none placeholder:text-slate-400 h-20"
                    placeholder="Korte toelichting..."
                  ></textarea>
                </div>

                <div className="pt-2">
                  <Button type="submit" fullWidth size="lg" className="text-base mb-3 shadow-lg shadow-brand-900/10">
                    Verstuur aanvraag
                  </Button>
                  <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1.5">
                    <Lock size={10} />
                    Uw gegevens worden veilig en versleuteld verstuurd.
                  </p>
                </div>
              </form>
            )}
          </div>

        </div>
      </div>
      
      {/* Bottom Footer Bar */}
      <div className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col md:flex-row justify-between items-center gap-6 text-sm text-slate-500">
          <p className="order-2 md:order-1">&copy; {new Date().getFullYear()} LetselHulp Direct. Alle rechten voorbehouden.</p>
          
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 order-1 md:order-2">
             <div className="flex items-center gap-6">
                <a href="#" className="hover:text-slate-800 transition-colors">Privacybeleid</a>
                <a href="#" className="hover:text-slate-800 transition-colors">Algemene Voorwaarden</a>
             </div>
             <FixelsBadge />
          </div>
        </div>
      </div>
    </footer>
  );
};